import {
  SelectedManualAction,
  SelectedPartAction,
  SelectedProposalPackage,
  ServiceAction,
} from "@repo/common"
import { Separator } from "@repo/ui/components/ui/separator"
import {
  findProductDecision,
  isActionSelected,
  updateProductDecisionInForm,
} from "@repo/ui/lib/product-choice-utils"
import { useContext } from "react"
import { useTranslations } from "use-intl"
import { formContext } from "../hooks/form-contexts"
import { ProductChoiceHeader } from "./product-choice-header"
import { ServiceProposalProductChoiceCard } from "./service-proposal-product-choice-card"

interface ProductChoice {
  id: string
  comment: string
  recommendedAlternative: {
    id: string
    title: string
    price: number
    ean: string | null
    needsOrdering?: boolean
    expectedDeliveryDate?: string | null
  }
  otherAlternatives: Array<{
    id: string
    title: string
    price: number
    ean: string | null
    needsOrdering?: boolean
    expectedDeliveryDate?: string | null
  }>
}

interface ActionWithProductChoices {
  partId?: string // For part actions
  id?: string // For manual actions
  partName?: string
  title?: string
  action?: string
  partImage?: string // For part actions
  productChoices: ProductChoice[]
}

// Using common types: SelectedPartAction, SelectedManualAction, SelectedProposalPackage

export interface ServiceProposalProductChoicesFieldProps {
  availableActions: ActionWithProductChoices[]
  formValues?: Record<
    string,
    | SelectedPartAction[]
    | SelectedManualAction[]
    | SelectedProposalPackage[]
    | string
    | undefined
  >
  onUpdateField?: {
    selectedPartActions: (
      updater: (prev: SelectedPartAction[]) => SelectedPartAction[],
    ) => void
    selectedManualActions: (
      updater: (prev: SelectedManualAction[]) => SelectedManualAction[],
    ) => void
    selectedPackages: (
      updater: (prev: SelectedProposalPackage[]) => SelectedProposalPackage[],
    ) => void
  }
}

export const ServiceProposalProductChoicesField = ({
  availableActions,
  formValues,
  onUpdateField,
}: ServiceProposalProductChoicesFieldProps) => {
  // Fallback to form context if props are not provided (for when used as registered field component)
  const t = useTranslations("ui.proposalForm")
  const form = useContext(formContext)
  const actualFormValues = formValues || form?.state.values

  const actualUpdateField = onUpdateField || {
    selectedPartActions: (
      updater: (prev: SelectedPartAction[]) => SelectedPartAction[],
    ) => form?.setFieldValue("selectedPartActions", updater),
    selectedManualActions: (
      updater: (prev: SelectedManualAction[]) => SelectedManualAction[],
    ) => form?.setFieldValue("selectedManualActions", updater),
    selectedPackages: (
      updater: (prev: SelectedProposalPackage[]) => SelectedProposalPackage[],
    ) => form?.setFieldValue("selectedPackages", updater),
  }

  if (!actualFormValues) {
    return <div className="text-destructive">{t("formStateUnavailable")}</div>
  }

  // Filter actions to only include those that are selected
  const selectedAvailableActions = availableActions.filter((action) => {
    const actionId = action.partId || action.id!
    const isManualAction = !!action.id && !action.partId
    return isActionSelected(actualFormValues, actionId, isManualAction)
  })

  const allProductChoices = selectedAvailableActions.flatMap((action) =>
    action.productChoices.map((choice) => ({
      actionId: action.partId || action.id!,
      actionName: action.partName || action.title!,
      actionType: action.action || action.title || "Unknown",
      partImage: action.partImage,
      isManualAction: !!action.id && !action.partId,
      choiceId: choice.id,
      comment: choice.comment,
      productAlternatives: [
        { ...choice.recommendedAlternative, isRecommended: true },
        ...choice.otherAlternatives.map((alt) => ({
          ...alt,
          isRecommended: false,
        })),
      ],
    })),
  )

  return (
    <div className="space-y-2">
      {allProductChoices.map((productChoice, index) => (
        <div key={`${productChoice.actionId}-${productChoice.choiceId}`}>
          {/* Action Header */}
          {productChoice.isManualAction ? (
            <ProductChoiceHeader
              variant="manual-action"
              title={productChoice.actionName}
            />
          ) : (
            <ProductChoiceHeader
              variant="part-action"
              partName={productChoice.actionName}
              partAction={productChoice.actionType as ServiceAction}
              partImage={productChoice.partImage || ""}
            />
          )}

          {/* Group Comment */}
          {productChoice.comment && (
            <p className="text-sm text-muted-foreground px-1 mb-2">
              {productChoice.comment}
            </p>
          )}

          {/* Product Alternatives */}
          <div className="space-y-2">
            {productChoice.productAlternatives
              .sort((a, b) => Number(a.price) - Number(b.price))
              .map((product) => {
                const selectedDecision = findProductDecision(
                  actualFormValues,
                  productChoice.actionId,
                  productChoice.choiceId,
                  productChoice.isManualAction,
                )

                const isSelected = selectedDecision === product.id

                return (
                  <ServiceProposalProductChoiceCard
                    key={product.id}
                    data={product}
                    selected={isSelected}
                    onSelect={() => {
                      updateProductDecisionInForm(
                        actualFormValues,
                        actualUpdateField,
                        productChoice.actionId,
                        productChoice.choiceId,
                        product.id,
                        productChoice.isManualAction,
                      )
                    }}
                  />
                )
              })}
          </div>

          {/* Separator between product choices (except for last one) */}
          {index < allProductChoices.length - 1 && (
            <div className="my-6">
              <Separator />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
